/* Hallmark · genre: editorial · macrostructure: Marquee Hero · theme: project tokens (orange --primary)
 * nav: n/a (WebLayout) · footer: n/a (WebLayout) · enrichment: voucher.image, else Tier A CSS art
 * archetypes: H1 Marquee (xl, left-bias, rule-below) · S2 Hanging heads · F6 card grid · F4 steps · C3 link CTAs
 * pre-emit critique: P5 H4 E4 S5 R5 V4
 */
import VoucherDetailInertiaController from '@/actions/App/Http/Controllers/VoucherDetailInertiaController';
import { SectionHeader } from '@/components/search/section-header';
import { RichContentRenderer } from '@/components/seo-content-renderer';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionRail } from '@/components/common/section-rail';
import { VoucherCard } from '@/components/common/voucher-card';
import { NewsletterSection } from '@/components/web/home/newsletter-section';
import { WebBreadcrumb } from '@/components/web/layouts/web-breadcrumb';
import WebLayout from '@/components/web/layouts/web-layout';
import { ApplicableServices } from '@/components/web/voucher/applicable-services';
import { VoucherHero } from '@/components/web/voucher/voucher-hero';
import { VoucherSteps } from '@/components/web/voucher/voucher-steps';
import { getImageUrl } from '@/lib/image';
import { cn } from '@/lib/utils';
import { type VoucherDetailPageProps } from '@/types/voucher';
import { Deferred, Head } from '@inertiajs/react';
import { useMemo } from 'react';

export default function VoucherShow({
  pageTitle,
  seoDescription,
  voucher,
  state,
  redeemable,
  scope,
  target,
  applicability,
  related,
}: VoucherDetailPageProps) {
  // Wayfinder hands back a path; canonical and og:url have to be absolute, and this
  // renders under SSR where `window` does not exist.
  const absolute = (path: string) => `${import.meta.env.VITE_APP_URL ?? ''}${path}`;

  const canonicalUrl = absolute(VoucherDetailInertiaController.show(voucher.code).url);
  const ogImage = voucher.image ? getImageUrl(voucher.image, 'voucher') : null;

  // An offer nobody can still claim keeps its page (old links, old emails) but
  // leaves the index — it is thin content from a crawler's point of view.
  const isIndexable = state === 'valid';

  const breadcrumbItems = useMemo(
    () => [
      { title: 'Ưu đãi', href: canonicalUrl },
      { title: voucher.title || voucher.code, href: canonicalUrl },
    ],
    [canonicalUrl, voucher.title, voucher.code],
  );

  return (
    <WebLayout>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={seoDescription} />
        {!isIndexable && <meta name="robots" content="noindex,follow" />}
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}

        <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={seoDescription} />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Offer',
            name: voucher.title || `Ưu đãi ${voucher.code}`,
            description: seoDescription,
            url: canonicalUrl,
            ...(ogImage ? { image: ogImage } : {}),
            ...(voucher.expires_at ? { validThrough: voucher.expires_at } : {}),
            availability: state === 'valid' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            ...(target ? { areaServed: target.name } : {}),
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.title,
              item: item.href,
            })),
          })}
        </script>
      </Head>

      <VoucherHero voucher={voucher} state={state} redeemable={redeemable} scope={scope} target={target} />

      {/* Marquee's mandated hero/below-fold divider. */}
      <hr className="border-primary/30 border-t-2" />

      <div className="container mx-auto max-w-6xl px-4 py-4">
        <WebBreadcrumb items={breadcrumbItems} className="px-0 text-xs" />
      </div>

      <div className="container mx-auto max-w-6xl space-y-14 px-4 pt-6 pb-20">
        <section id="applicable" className="scroll-mt-24">
          <SectionHeader title="Ưu đãi này áp dụng cho" className="mb-6" />

          {/* Both deferred props share Inertia's default group, so one fallback covers
              the pair. The skeleton is sized to the loaded grid to keep CLS at zero. */}
          <Deferred
            data={['applicability', 'related']}
            fallback={
              <div>
                <Skeleton className="mb-8 h-5 w-full max-w-lg" />
                <Skeleton className="mb-4 h-6 w-32" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl border border-(--pill-border) bg-white p-5">
                      <Skeleton className="h-32 w-full rounded-lg" />
                      <Skeleton className="mt-3 h-5 w-3/4" />
                      <Skeleton className="mt-2 h-4 w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            {applicability ? <ApplicableServices applicability={applicability} /> : null}
          </Deferred>
        </section>

        {voucher.description && (
          <section id="terms" className="scroll-mt-24">
            <SectionHeader title="Điều kiện áp dụng" className="mb-6" />
            <RichContentRenderer
              editorSerializedState={voucher.description}
              className={cn(
                'prose prose-gray max-w-[72ch]',
                'prose-headings:font-semibold prose-headings:scroll-mt-24 prose-headings:text-gray-900',
                'prose-h2:text-xl prose-h3:text-base prose-h3:mt-6',
                'prose-p:text-gray-700 prose-p:leading-relaxed',
                'prose-img:rounded-xl prose-img:shadow-sm prose-img:my-4',
                'prose-ul:pl-5 prose-li:text-gray-700',
                'prose-strong:font-semibold prose-strong:text-gray-900',
              )}
            />
          </section>
        )}

        <section id="how-to" className="scroll-mt-24">
          <SectionHeader title="Cách dùng mã" className="mb-6" />
          <div className="max-w-2xl">
            <VoucherSteps code={voucher.code} scope={scope} redeemable={redeemable} />
          </div>
        </section>

        {/* No <Deferred> wrapper: `related` shares the group the section above already
            requests, and a rail is not worth reserving space for before it exists. */}
        {related && related.length > 0 && (
          <SectionRail title="Ưu đãi khác" subtitle="Mã giảm giá đang áp dụng" itemClassName="w-[260px] md:w-[300px]">
            {related.map((other) => (
              <VoucherCard
                key={other.id}
                voucher={other}
                href={VoucherDetailInertiaController.show(other.code).url}
                className="aspect-[3/2]"
              />
            ))}
          </SectionRail>
        )}
      </div>

      <NewsletterSection />
    </WebLayout>
  );
}
